// src/pages/legal/RegulatoryInfo.js
import React from 'react'
import { Box, Container, Typography, Card, CardContent, Divider, Stack } from '@mui/material'
import { useLanguage } from '../../contexts/LanguageContext'

export default function RegulatoryInfo() {
    const effectiveDate = 'January 5, 2026' // change when needed

    const { t, lang } = useLanguage()
    const isFr = lang === 'fr'
    const tt = (key, enFallback, frFallback) => t(key, null, isFr ? frFallback : enFallback)

    // Replace these with real info when available.
    // Do NOT publish fake license numbers.
    const licenseStatus = tt(
        'legal.regulatory.licenseStatus',
        'Licensing and registrations may vary by region.',
        'Les licences et enregistrements peuvent varier selon la région.'
    )

    const serviceRegions = tt(
        'legal.regulatory.serviceRegions',
        'Sierra Leone, Liberia, Guinea (initial rollout)',
        'Sierra Leone, Liberia, Guinée (déploiement initial)'
    )

    const disclosures = [
        tt(
            'legal.regulatory.disclosures.1',
            'Services may be provided directly by MojoPayr or through approved partners.',
            'Les services peuvent être fournis directement par MojoPayr ou via des partenaires approuvés.'
        ),
        tt(
            'legal.regulatory.disclosures.2',
            'Transactions may be delayed or declined based on verification, risk checks, or legal requirements.',
            'Les transactions peuvent être retardées ou refusées selon la vérification, les contrôles de risque ou les obligations légales.'
        ),
        tt(
            'legal.regulatory.disclosures.3',
            'Fees and limits may vary by country, payment method, and transaction type.',
            'Les frais et les limites peuvent varier selon le pays, le mode de paiement et le type de transaction.'
        ),
    ]

    const sectionTitleSx = {
        fontWeight: 900,
        color: 'rgba(255,255,255,0.92)',
        mt: 2.4,
        mb: 0.8,
        letterSpacing: 0.2,
    }

    const bodySx = {
        color: 'rgba(255,255,255,0.78)',
        lineHeight: 1.9,
        fontSize: { xs: '0.98rem', md: '1.02rem' },
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
    }

    return (
        <Box sx={{ py: { xs: 5, md: 7 } }}>
            <Container maxWidth="md">
                <Typography component="h1" variant="h4" sx={{ fontWeight: 950, color: 'white', mb: 1 }}>
                    {tt('legal.regulatory.title', 'Regulatory Information', 'Informations réglementaires')}
                </Typography>

                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    sx={{ mb: 3, alignItems: { sm: 'center' } }}
                >
                    <Typography sx={{ color: 'rgba(255,255,255,0.72)' }}>
                        {tt(
                            'legal.regulatory.subtitle',
                            'Licensing details and required disclosures.',
                            'Détails de licence et mentions obligatoires.'
                        )}
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.55)' }}>
                        {tt('legal.regulatory.effective', 'Effective Date:', 'Date d’effet :')}{' '}
                        {effectiveDate}
                    </Typography>
                </Stack>

                <Card
                    sx={{
                        borderRadius: 6,
                        background: 'rgba(255,255,255,0.06)',
                        border: '1px solid rgba(255,255,255,0.10)',
                        backdropFilter: 'blur(10px)',
                    }}
                >
                    <CardContent sx={{ p: { xs: 2.4, md: 3.2 } }}>
                        <Typography sx={bodySx}>
                            {tt(
                                'legal.regulatory.intro',
                                'This page provides general regulatory disclosures for MojoPayr. It does not replace legal advice and may be updated as licensing and partner arrangements change.',
                                'Cette page présente des informations réglementaires générales pour MojoPayr. Elle ne remplace pas un avis juridique et peut être mise à jour en fonction des licences et des partenariats.'
                            )}
                        </Typography>

                        <Divider sx={{ my: 2.2, borderColor: 'rgba(255,255,255,0.10)' }} />

                        <Typography sx={sectionTitleSx}>
                            {tt('legal.regulatory.s1', '1. Service Regions', '1. Zones de service')}
                        </Typography>
                        <Typography sx={bodySx}>{serviceRegions}</Typography>

                        <Typography sx={sectionTitleSx}>
                            {tt('legal.regulatory.s2', '2. Licensing and Registration', '2. Licence et enregistrement')}
                        </Typography>
                        <Typography sx={bodySx}>{licenseStatus}</Typography>

                        <Typography sx={sectionTitleSx}>
                            {tt('legal.regulatory.s3', '3. Disclosures', '3. Mentions')}
                        </Typography>
                        <Box component="ul" sx={{ mt: 1.2, mb: 0, pl: 2.2, ...bodySx }}>
                            {disclosures.map((d, idx) => (
                                <li key={idx} style={{ marginBottom: 10 }}>
                                    {d}
                                </li>
                            ))}
                        </Box>

                        <Typography sx={sectionTitleSx}>
                            {tt('legal.regulatory.s4', '4. Consumer Protection', '4. Protection du consommateur')}
                        </Typography>
                        <Typography sx={bodySx}>
                            {tt(
                                'legal.regulatory.consumer',
                                'If you believe your account has been accessed without permission, contact support as soon as possible. We may request details to investigate and take steps to protect the account.',
                                'Si vous pensez que votre compte a été consulté sans autorisation, contactez le support dès que possible. Nous pouvons demander des informations pour enquêter et protéger le compte.'
                            )}
                        </Typography>

                        <Typography sx={sectionTitleSx}>
                            {tt('legal.regulatory.s5', '5. Updates', '5. Mises à jour')}
                        </Typography>
                        <Typography sx={bodySx}>
                            {tt(
                                'legal.regulatory.updates',
                                'We may update this page as services expand and as regulatory requirements change.',
                                'Nous pouvons mettre à jour cette page à mesure que les services s’étendent et que les exigences réglementaires évoluent.'
                            )}
                        </Typography>
                    </CardContent>
                </Card>
            </Container>
        </Box>
    )
}

